import React, { createContext, useCallback, useContext, useMemo, ReactNode } from "react";
import { ExtractionConfidence } from "@investorRequest/models/bankExtraction";

export interface FieldExtractionMeta {
  confidence: ExtractionConfidence;
  candidates?: string[] | null;
  showEditedBadge: boolean;
  isAmbiguous: boolean;
  wasAcknowledged: boolean;
}

interface BankExtractionFormContextValue {
  isActive: boolean;
  getFieldMeta: (fieldName: string) => FieldExtractionMeta | null;
  onFieldChange: (fieldName: string, value: string) => void;
  onAmbiguousAcknowledge: (fieldName: string) => void;
}

const BankExtractionFormContext = createContext<BankExtractionFormContextValue>({
  isActive: false,
  getFieldMeta: () => null,
  onFieldChange: () => undefined,
  onAmbiguousAcknowledge: () => undefined,
});

export const useBankExtractionForm = () => useContext(BankExtractionFormContext);

interface ProviderProps {
  children: ReactNode;
  isActive: boolean;
  fieldConfidence: Record<string, ExtractionConfidence>;
  extractionEditedFields: Record<string, boolean>;
  ambiguousAcknowledgedFields: Record<string, boolean>;
  candidatesByField: Record<string, string[] | null | undefined>;
  onFieldChange: (fieldName: string, value: string) => void;
  onAmbiguousAcknowledge: (fieldName: string) => void;
}

export const BankExtractionFormProvider = ({
  children,
  isActive,
  fieldConfidence,
  extractionEditedFields,
  ambiguousAcknowledgedFields,
  candidatesByField,
  onFieldChange,
  onAmbiguousAcknowledge,
}: ProviderProps) => {
  const getFieldMeta = useCallback(
    (fieldName: string): FieldExtractionMeta | null => {
      if (!isActive || !(fieldName in fieldConfidence)) return null;

      const confidence = fieldConfidence[fieldName];
      const candidates = candidatesByField[fieldName];
      const isAmbiguous = Array.isArray(candidates) && candidates.length > 1;

      return {
        confidence,
        candidates,
        showEditedBadge: !!extractionEditedFields[fieldName],
        isAmbiguous,
        wasAcknowledged: !!ambiguousAcknowledgedFields[fieldName],
      };
    },
    [ambiguousAcknowledgedFields, candidatesByField, extractionEditedFields, fieldConfidence, isActive]
  );

  const value = useMemo(
    () => ({
      isActive,
      getFieldMeta,
      onFieldChange,
      onAmbiguousAcknowledge,
    }),
    [getFieldMeta, isActive, onAmbiguousAcknowledge, onFieldChange]
  );

  return <BankExtractionFormContext.Provider value={value}>{children}</BankExtractionFormContext.Provider>;
};
